import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type PageId =
  | 'dashboard'
  | 'contacts'
  | 'pipeline'
  | 'policies'
  | 'tasks'
  | 'calendar'
  | 'communications'
  | 'retention'
  | 'automations'
  | 'analytics'
  | 'reports'
  | 'documents'
  | 'licensing'
  | 'compliance'
  | 'accounting'
  | 'team'
  | 'settings'
  | 'security';

interface ModalState {
  newContact: boolean;
  contactDetail: string | null; // Contact ID or null
  newPolicy: boolean;
  newTask: boolean;
  confirmDialog: {
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'danger' | 'warning' | 'primary' | 'success';
    onConfirm: (() => void) | null;
  };
}

interface UIState {
  // Navigation
  currentPage: PageId;
  sidebarCollapsed: boolean;

  // Modals
  modals: ModalState;

  // Search
  globalSearch: string;

  // Notifications
  notificationCount: number;

  // Recent sale celebration
  recentSale: {
    contactName: string;
    productType: string;
    commission: number;
  } | null;

  // Actions
  setCurrentPage: (page: PageId) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Modal actions
  openNewContactModal: () => void;
  closeNewContactModal: () => void;
  openContactDetail: (contactId: string) => void;
  closeContactDetail: () => void;
  openNewPolicyModal: () => void;
  closeNewPolicyModal: () => void;
  openNewTaskModal: () => void;
  closeNewTaskModal: () => void;
  openConfirmDialog: (options: {
    title: string;
    message: string;
    variant?: 'danger' | 'warning' | 'primary' | 'success';
    onConfirm: () => void;
  }) => void;
  closeConfirmDialog: () => void;

  // Search
  setGlobalSearch: (search: string) => void;

  // Notifications
  setNotificationCount: (count: number) => void;
  incrementNotificationCount: () => void;
  clearNotifications: () => void;

  // Recent sale
  setRecentSale: (sale: UIState['recentSale']) => void;
  clearRecentSale: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, _get) => ({
      currentPage: 'dashboard',
      sidebarCollapsed: false,
      modals: {
        newContact: false,
        contactDetail: null,
        newPolicy: false,
        newTask: false,
        confirmDialog: {
          isOpen: false,
          title: '',
          message: '',
          variant: 'primary',
          onConfirm: null
        }
      },
      globalSearch: '',
      notificationCount: 0,
      recentSale: null,

      setCurrentPage: (page) => {
        set({ currentPage: page });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },

      openNewContactModal: () => {
        set((state) => ({
          modals: { ...state.modals, newContact: true }
        }));
      },

      closeNewContactModal: () => {
        set((state) => ({
          modals: { ...state.modals, newContact: false }
        }));
      },

      openContactDetail: (contactId) => {
        set((state) => ({
          modals: { ...state.modals, contactDetail: contactId }
        }));
      },

      closeContactDetail: () => {
        set((state) => ({
          modals: { ...state.modals, contactDetail: null }
        }));
      },

      openNewPolicyModal: () => {
        set((state) => ({
          modals: { ...state.modals, newPolicy: true }
        }));
      },

      closeNewPolicyModal: () => {
        set((state) => ({
          modals: { ...state.modals, newPolicy: false }
        }));
      },

      openNewTaskModal: () => {
        set((state) => ({
          modals: { ...state.modals, newTask: true }
        }));
      },

      closeNewTaskModal: () => {
        set((state) => ({
          modals: { ...state.modals, newTask: false }
        }));
      },

      openConfirmDialog: ({ title, message, variant = 'primary', onConfirm }) => {
        set((state) => ({
          modals: {
            ...state.modals,
            confirmDialog: {
              isOpen: true,
              title,
              message,
              variant,
              onConfirm
            }
          }
        }));
      },

      closeConfirmDialog: () => {
        set((state) => ({
          modals: {
            ...state.modals,
            confirmDialog: {
              ...state.modals.confirmDialog,
              isOpen: false,
              onConfirm: null
            }
          }
        }));
      },

      setGlobalSearch: (search) => {
        set({ globalSearch: search });
      },

      setNotificationCount: (count) => {
        set({ notificationCount: count });
      },

      incrementNotificationCount: () => {
        set((state) => ({ notificationCount: state.notificationCount + 1 }));
      },

      clearNotifications: () => {
        set({ notificationCount: 0 });
      },

      setRecentSale: (sale) => {
        set({ recentSale: sale });
      },

      clearRecentSale: () => {
        set({ recentSale: null });
      }
    }),
    {
      name: 'alvo-ui',
      partialize: (state) => ({
        currentPage: state.currentPage,
        sidebarCollapsed: state.sidebarCollapsed
      })
    }
  )
);

// Convenience hooks
export const useCurrentPage = () => useUIStore((state) => state.currentPage);
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useGlobalSearch = () => useUIStore((state) => state.globalSearch);
